/*
 * LandingNavbar Component
 *
 * Themed navigation bar for subdomain landing pages with responsive design.
 * Uses CSS variables for theming and supports both desktop and mobile navigation.
 * Includes support for dynamic sections including appointments, reviews, blogs, posts, gallery, and more.
 */

import { useState } from "react";
import { Menu, X, Phone, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate, useParams } from "react-router-dom";
import {
  LANDING_PAGE_SECTIONS,
  getSectionsByPriority,
  shouldUseDropdownMenu,
  type SectionAvailabilityData,
} from "../landingPageSectionsConfig";
import { proxyGoogleImage } from "../lib/googleImageProxy";

interface LandingNavbarProps {
  businessName: string;
  logoUrl?: string;
  pages: any[];
  theme: any;
  hasPublishedPosts?: boolean;
  hasReviews?: boolean;
  hasBlogs?: boolean;
  hasProductsServices?: boolean;
  hasServices?: boolean;
  hasGallery?: boolean;
  hasSocial?: boolean;
  hasAppointment?: boolean;
  phoneNumber?: string;
  editMode?: boolean;
  onTrackPhoneClick?: (phone: string) => void;
  location?: any;
  workspace?: any;
  getCleanedServices?: (services: any[]) => any[];
  customizations?: any;
}

const LandingNavbar = ({ businessName, logoUrl, pages, theme, hasPublishedPosts, hasReviews, hasBlogs, hasProductsServices, hasServices, hasGallery, hasSocial, hasAppointment, phoneNumber, editMode = false, onTrackPhoneClick, location, workspace, getCleanedServices, customizations }: LandingNavbarProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const { subdomain } = useParams<{ subdomain: string }>();

  console.log('LandingNavbar - logoUrl received:', logoUrl);
  console.log('LandingNavbar - businessName:', businessName);
  console.log('LandingNavbar - hasBlogs:', hasBlogs);
  console.log('LandingNavbar - hasReviews:', hasReviews);
  console.log('LandingNavbar - hasPublishedPosts:', hasPublishedPosts);
  console.log('LandingNavbar - hasProductsServices:', hasProductsServices);
  console.log('LandingNavbar - hasGallery:', hasGallery);

  // Prepare availability data for config-driven rendering
  // Include customizations to respect section visibility settings (show_services, show_reviews, etc.)
  const availabilityData: SectionAvailabilityData = {
    location,
    workspace,
    hasReviews,
    hasPublishedPosts,
    hasBlogs,
    hasProductsServices,
    customizations,
    getCleanedServices,
  };

  // Get sections from config
  const prioritySections = getSectionsByPriority(1, availabilityData);
  const secondarySections = getSectionsByPriority(2, availabilityData);
  const useDropdownMenu = shouldUseDropdownMenu(availabilityData);

  // Calculate ACTUALLY visible nav items for business name width
  // Only count Priority 1 tabs + tabs shown directly (not in dropdown) + buttons + More button if present
  let visibleNavItems = prioritySections.length;

  // Add Priority 2 tabs if shown directly (not in dropdown)
  if (!useDropdownMenu) {
    visibleNavItems += secondarySections.length;
  } else if (secondarySections.length > 0) {
    visibleNavItems += 1; // "More" dropdown button
  }

  // Add action buttons
  if (hasAppointment) visibleNavItems += 1; // Book Now button
  if (phoneNumber) visibleNavItems += 1; // Call button

  // Dynamically set max-width based on number of ACTUALLY visible tabs
  // Only apply on desktop (lg:) since mobile uses hamburger menu
  const getBusinessNameMaxWidth = () => {
    if (visibleNavItems <= 3) return 'lg:max-w-[600px]';
    if (visibleNavItems <= 5) return 'lg:max-w-[500px]';
    if (visibleNavItems <= 7) return 'lg:max-w-[450px]';
    return 'lg:max-w-[400px]';
  };

  // Check if we're on an actual subdomain (subdomain.localhost) vs testing route (/subdomain/:subdomain)
  const isOnSubdomain = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      // subdomain.localhost or subdomain.my-domain.com
      return parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www';
    }
    return false;
  };

  const navigateToSection = (sectionId: string) => {
    // In edit mode, scroll to section instead of navigating
    if (editMode) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setMobileMenuOpen(false);
      return;
    }

    // Determine the base path based on whether we're on actual subdomain or testing route
    const basePath = isOnSubdomain() ? '' : `/subdomain/${subdomain}`;

    // Find section config to get the correct route
    const sectionConfig = LANDING_PAGE_SECTIONS.find(s => s.id === sectionId);

    if (sectionConfig) {
      navigate(`${basePath}${sectionConfig.routePath}`);
    } else {
      // Fallback for unknown sections
      navigate(`${basePath}/${sectionId}`);
    }

    setMobileMenuOpen(false);
  };

  return (
    <nav
      className="sticky top-0 z-50 bg-white shadow-sm border-b-2"
      style={{ borderBottomColor: "var(--primary)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center cursor-pointer min-w-0 flex-1 lg:flex-none" onClick={() => !editMode && navigate(isOnSubdomain() ? '/' : `/subdomain/${subdomain}`)}>
            {logoUrl && !logoError && (
              <img
                src={proxyGoogleImage(logoUrl, 200)}
                alt={businessName}
                className="h-12 w-auto mr-3 object-contain flex-shrink-0"
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                onError={() => {
                  // Fires only when the (already proxied) URL genuinely
                  // fails — treat as a real problem worth logging.
                  console.error('Logo failed to load after proxy:', logoUrl);
                  setLogoError(true);
                }}
                onLoad={() => console.log('Logo loaded successfully')}
              />
            )}
            <span className={`font-bold text-xl ${getBusinessNameMaxWidth()} truncate min-w-0`}>{businessName}</span>
          </div>

          {/* Desktop Navigation - Config-driven */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Priority 1: Always visible tabs (from config) */}
            {prioritySections.map((section) => (
              <button
                key={section.id}
                onClick={() => navigateToSection(section.id)}
                className="transition-colors hover:opacity-80 text-sm"
                style={{ color: "var(--primary)" }}
              >
                {section.navLabel}
              </button>
            ))}

            {/* Priority 2: Show directly when not crowded, or in dropdown when crowded */}
            {!useDropdownMenu && secondarySections.map((section) => (
              <button
                key={section.id}
                onClick={() => navigateToSection(section.id)}
                className="transition-colors hover:opacity-80 text-sm"
                style={{ color: "var(--primary)" }}
              >
                {section.navLabel}
              </button>
            ))}

            {/* "More" Dropdown Menu - shown when navbar is crowded */}
            {useDropdownMenu && secondarySections.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="transition-colors hover:opacity-80 text-sm flex items-center gap-1"
                    style={{ color: "var(--primary)" }}
                  >
                    More <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {secondarySections.map((section) => (
                    <DropdownMenuItem
                      key={section.id}
                      onClick={() => navigateToSection(section.id)}
                    >
                      {section.navLabel}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {hasAppointment && (
              <Button
                onClick={() => {
                  if (editMode) {
                    const appointmentSection = document.getElementById('appointment');
                    if (appointmentSection) {
                      appointmentSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  } else {
                    navigateToSection('appointment');
                  }
                }}
                style={{
                  backgroundColor: "var(--primary)",
                  borderColor: "var(--primary)",
                  color: "var(--primary-foreground)",
                }}
                className="hover:opacity-90"
              >
                Book Now
              </Button>
            )}
            {phoneNumber && (
              <Button
                onClick={() => {
                  if (onTrackPhoneClick) {
                    onTrackPhoneClick(phoneNumber);
                  }
                  window.location.href = `tel:${phoneNumber}`;
                }}
                variant="outline"
                style={{
                  borderColor: "var(--primary)",
                  color: "var(--primary)",
                }}
                className="hover:opacity-90"
              >
                <Phone className="h-4 w-4 mr-2" />
                Call {phoneNumber}
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation - Config-driven */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* All sections from config (no dropdown in mobile) */}
            {[...prioritySections, ...secondarySections].map((section) => (
              <button
                key={section.id}
                onClick={() => navigateToSection(section.id)}
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 w-full text-left"
              >
                {section.navLabel}
              </button>
            ))}
            {hasAppointment && (
              <Button
                onClick={() => {
                  if (editMode) {
                    const appointmentSection = document.getElementById('appointment');
                    if (appointmentSection) {
                      appointmentSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  } else {
                    navigateToSection('appointment');
                  }
                  setMobileMenuOpen(false);
                }}
                className="w-full mt-2"
                style={{
                  backgroundColor: "var(--primary)",
                  borderColor: "var(--primary)",
                  color: "var(--primary-foreground)",
                }}
              >
                Book Now
              </Button>
            )}
            {phoneNumber && (
              <Button
                onClick={() => {
                  if (onTrackPhoneClick) {
                    onTrackPhoneClick(phoneNumber);
                  }
                  window.location.href = `tel:${phoneNumber}`;
                  setMobileMenuOpen(false);
                }}
                variant="outline"
                className="w-full mt-2"
                style={{
                  borderColor: "var(--primary)",
                  color: "var(--primary)",
                }}
              >
                <Phone className="h-4 w-4 mr-2" />
                Call {phoneNumber}
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default LandingNavbar;

/*
 * End of LandingNavbar Component
 */